import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  GEE_ALPHAEARTH_LATEST_KNOWN_AVAILABLE_YEAR,
  GEE_SCENARIO_YEARS,
  getDefaultScenarioYear,
  MAP_DEFAULTS,
} from '@/config/map';

import type {
  MapState,
  MapTab,
  GEEParams,
  GeeStatus,
  MapViewState,
  MapViewSource,
  MapInspectorTab,
  FloodScrubberRangeHours,
  FloodScrubberStepHours,
} from '../types/map.types';
import { createDefaultIoTStationTypeVisibility } from '../types/map.types';
import { FLOOD_DEPTH_DISPLAY_FILTER_MAX_M } from '../utils/floodDepthScale';

const currentYear = new Date().getFullYear();

const defaultMapView: MapViewState = {
  longitude: MAP_DEFAULTS.CENTER.lng,
  latitude: MAP_DEFAULTS.CENTER.lat,
  zoom: MAP_DEFAULTS.ZOOM,
  bearing: 0,
  pitch: 0,
};

const initialState: MapState = {
  activeTab: '2d',
  isLive: GEE_ALPHAEARTH_LATEST_KNOWN_AVAILABLE_YEAR === currentYear,
  selectedRegionId: null,
  selectedFloodPointId: null,
  geeParams: {
    year: getDefaultScenarioYear(),
    alphaEarthReferenceDate: null,
    index: 'RGB',
    spectralBands: null,
    minVal: -0.3,
    maxVal: 0.3,
  },
  geeStatus: 'idle',
  geeStatusYear: null,
  floodDepthFilter: [0, FLOOD_DEPTH_DISPLAY_FILTER_MAX_M],
  terrainExaggeration: 2.0,
  mapView: defaultMapView,
  mapViewSource: '2d',
  showFloodExtentAnalysis: false,
  showFloodDepthAnalysis: false,
  inspectorTab: 'search',
  inspectorExpanded: true,
  showIoTStationsHeatmap: false,
  iotStationTypeVisibility: createDefaultIoTStationTypeVisibility(),
  stationFlyTo: null,
  iotStationDetailOpen: null,
  floodScrubber: { rangeHours: 24, stepHours: 1 },
  weatherPoint: null,
  weatherPointUserPinned: false,
};

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<MapTab>) {
      state.activeTab = action.payload;
      if (action.payload === '2d') {
        state.inspectorTab = 'search';
      } else {
        state.inspectorTab = 'filters';
      }
    },
    setSelectedRegionId(state, action: PayloadAction<string | null>) {
      state.selectedRegionId = action.payload;
    },
    setIsLive(state, action: PayloadAction<boolean>) {
      state.isLive = action.payload;
      if (action.payload) {
        const cy = new Date().getFullYear();
        state.geeParams.year = GEE_SCENARIO_YEARS.includes(cy) ? cy : getDefaultScenarioYear();
      }
    },
    setGeeParams(state, action: PayloadAction<Partial<GEEParams>>) {
      Object.assign(state.geeParams, action.payload);
    },
    setGeeStatus(state, action: PayloadAction<{ status: GeeStatus; year: number | null }>) {
      state.geeStatus = action.payload.status;
      state.geeStatusYear = action.payload.year;
    },
    setFloodDepthFilter(state, action: PayloadAction<[number, number]>) {
      state.floodDepthFilter = action.payload;
    },
    setTerrainExaggeration(state, action: PayloadAction<number>) {
      state.terrainExaggeration = action.payload;
    },
    setSelectedFloodPointId(state, action: PayloadAction<string | null>) {
      state.selectedFloodPointId = action.payload;
    },
    setMapView(state, action: PayloadAction<{ view: MapViewState; source: MapViewSource }>) {
      state.mapView = action.payload.view;
      state.mapViewSource = action.payload.source;
    },
    setShowFloodExtentAnalysis(state, action: PayloadAction<boolean>) {
      state.showFloodExtentAnalysis = action.payload;
    },
    setShowFloodDepthAnalysis(state, action: PayloadAction<boolean>) {
      state.showFloodDepthAnalysis = action.payload;
    },
    setInspectorTab(state, action: PayloadAction<MapInspectorTab>) {
      state.inspectorTab = action.payload;
    },
    setInspectorExpanded(state, action: PayloadAction<boolean>) {
      state.inspectorExpanded = action.payload;
    },
    setShowIoTStationsHeatmap(state, action: PayloadAction<boolean>) {
      state.showIoTStationsHeatmap = action.payload;
    },
    setIoTStationTypeVisible(state, action: PayloadAction<{ key: string; visible: boolean }>) {
      state.iotStationTypeVisibility[action.payload.key] = action.payload.visible;
    },
    setIoTStationTypesVisibility(
      state,
      action: PayloadAction<{ keys: string[]; visible: boolean }>,
    ) {
      action.payload.keys.forEach((key) => {
        state.iotStationTypeVisibility[key] = action.payload.visible;
      });
    },
    /**
     * Khôi phục tùy chọn trong panel lớp: GEE (chỉ số/palette mặc định), IoT heatmap + loại trạm.
     * Không đổi năm kịch bản, vị trí bản đồ hay bộ lọc độ sâu.
     */
    resetLayerPanelMapPreferences(state) {
      state.geeParams.alphaEarthReferenceDate = null;
      state.geeParams.index = 'RGB';
      state.geeParams.spectralBands = null;
      state.geeParams.minVal = -0.3;
      state.geeParams.maxVal = 0.3;
      state.showFloodExtentAnalysis = false;
      state.showFloodDepthAnalysis = false;
      state.showIoTStationsHeatmap = false;
      state.iotStationTypeVisibility = createDefaultIoTStationTypeVisibility();
    },
    requestStationFlyTo(
      state,
      action: PayloadAction<{ longitude: number; latitude: number; zoom: number }>,
    ) {
      const prev = state.stationFlyTo?.nonce ?? 0;
      state.stationFlyTo = {
        nonce: prev + 1,
        longitude: action.payload.longitude,
        latitude: action.payload.latitude,
        zoom: action.payload.zoom,
      };
    },
    requestOpenIotStationDetail(
      state,
      action: PayloadAction<{ stationId: string; initialRoutingKey?: string }>,
    ) {
      const prev = state.iotStationDetailOpen?.nonce ?? 0;
      const routingKey = action.payload.initialRoutingKey?.trim();
      state.iotStationDetailOpen = {
        nonce: prev + 1,
        stationId: action.payload.stationId,
        ...(routingKey ? { initialRoutingKey: routingKey } : {}),
      };
    },
    setFloodScrubberRangeHours(state, action: PayloadAction<FloodScrubberRangeHours>) {
      state.floodScrubber.rangeHours = action.payload;
    },
    setFloodScrubberStepHours(state, action: PayloadAction<FloodScrubberStepHours>) {
      state.floodScrubber.stepHours = action.payload;
    },
    setWeatherPoint(
      state,
      action: PayloadAction<{ lat: number; lng: number; userPinned?: boolean } | null>,
    ) {
      if (action.payload === null) {
        state.weatherPoint = null;
        state.weatherPointUserPinned = false;
        return;
      }
      state.weatherPoint = { lat: action.payload.lat, lng: action.payload.lng };
      if (action.payload.userPinned) state.weatherPointUserPinned = true;
    },
  },
});

export const {
  setActiveTab,
  setSelectedRegionId,
  setIsLive,
  setGeeParams,
  setGeeStatus,
  setFloodDepthFilter,
  setTerrainExaggeration,
  setSelectedFloodPointId,
  setMapView,
  setShowFloodExtentAnalysis,
  setShowFloodDepthAnalysis,
  setInspectorTab,
  setInspectorExpanded,
  setShowIoTStationsHeatmap,
  setIoTStationTypeVisible,
  setIoTStationTypesVisibility,
  resetLayerPanelMapPreferences,
  requestStationFlyTo,
  requestOpenIotStationDetail,
  setFloodScrubberRangeHours,
  setFloodScrubberStepHours,
  setWeatherPoint,
} = mapSlice.actions;
export default mapSlice.reducer;
